import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    students: null,
    teachers: null,
    courses: null,
    batches: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const loadStats = useCallback(async ({ isRefresh = false } = {}) => {
    const requestId = Date.now();
    requestIdRef.current = requestId;
    if (!isRefresh) {
      setLoading(true);
    }
    setError('');

    try {
      const res = await apiClient.get('/api/dashboard-stats/');
      if (requestIdRef.current !== requestId) return;

      const nextStats = res?.data?.stats || {};
      setStats({
        students: Number(nextStats.students) || 0,
        teachers: Number(nextStats.teachers) || 0,
        courses: Number(nextStats.courses) || 0,
        batches: Number(nextStats.batches) || 0,
      });
    } catch (e) {
      if (requestIdRef.current !== requestId) return;
      let message =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        'Could not load dashboard statistics.';
      if (!e?.response && (e?.message === 'Network Error' || e?.code === 'ERR_NETWORK')) {
        const apiUrl = apiClient.defaults.baseURL || 'https://edupravahaa-tech-admin.com';
        message = `Cannot reach the API at ${apiUrl}. Start the backend with: python manage.py runserver 8001`;
      }
      setError(message);
      setStats({
        students: 0,
        teachers: 0,
        courses: 0,
        batches: 0,
      });
    } finally {
      if (requestIdRef.current !== requestId) return;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadStats({ isRefresh: true });
    }, 45000);
    return () => window.clearInterval(intervalId);
  }, [loadStats]);

  const formatStat = (n) => {
    if (loading || n === null) return '…';
    return Number(n).toLocaleString();
  };

  const statsCards = [
    { label: 'Total Students', value: stats.students, icon: '👥' },
    { label: 'Total Teachers', value: stats.teachers, icon: '👨‍🏫' },
    { label: 'Courses', value: stats.courses, icon: '📚' },
    { label: 'Batches', value: stats.batches, icon: '📦' },
  ];

  const quickActions = [
    { id: 'course', label: 'Add New Course', path: '/courses/add' },
    { id: 'teacher', label: 'Add Teacher', path: '/teachers/add' },
    { id: 'batch', label: 'Add Batch', path: '/batches/add' },
    { id: 'student', label: 'Add Student', path: '/students/add' },
  ];

  const [activeQuickAction, setActiveQuickAction] = useState(null);

  const handleQuickAction = (action) => {
    setActiveQuickAction(action.id);
    navigate(action.path);
  };

  return (
    <div className="dashboard">
      <header className="page-header">
        <h1>Dashboard</h1>
      </header>

      {error ? (
        <p className="form-error" style={{ marginBottom: '16px' }}>
          {error}
        </p>
      ) : null}

      <div className="stats-grid">
        {statsCards.map((stat) => (
          <div key={stat.label} className="dashboard-card">
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <span className="stat-value">{formatStat(stat.value)}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card recent-activity">
          <h2>Getting started</h2>
          <ul className="activity-list">
            <li>
              <span className="activity-dot" />
              <span>Use Teachers and Courses to set up who teaches what.</span>
              <span className="activity-time"> </span>
            </li>
            <li>
              <span className="activity-dot" />
              <span>Create batches to schedule classes and capacity.</span>
              <span className="activity-time"> </span>
            </li>
            <li>
              <span className="activity-dot" />
              <span>Student enrollments are listed under the enrollments API (Students module coming soon).</span>
              <span className="activity-time"> </span>
            </li>
          </ul>
        </div>
        <div className="dashboard-card quick-actions">
          <h2>Quick actions</h2>
          <div className="action-buttons" role="group" aria-label="Quick actions">
            {quickActions.map((action) => (
              <button
                key={action.id}
                type="button"
                className={`quick-action-btn ${
                  activeQuickAction === action.id ? 'quick-action-btn--active' : ''
                }`}
                onClick={() => handleQuickAction(action)}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
